/**
 * BrokMessageList — renders the chat transcript.
 * Skips hidden user messages (kickoff seed).
 *
 * P2b: confirmation kind dispatches to InvoiceConfirmCard (for invoice tools) or
 * the generic ConfirmCard fallback. Threads onRueckfrage from BrokScreen.
 *
 * [Source: Story P1b spec §8, Story P2b spec §3.6]
 */
import { useEffect, useRef } from 'react';
import type { BrokUiMessage } from '../brok-chat.types';
import type { PendingWorkSummary, EntitySummary } from '@brokly/shared';
import { isInvoiceConfirmFacts } from '@brokly/shared';
import { PendingWorkCard } from './PendingWorkCard';
import { EntitySummaryCard } from './EntitySummaryCard';
import { ToolCallCard } from './ToolCallCard';
import { ConfirmCard } from './ConfirmCard';
import { InvoiceConfirmCard } from './InvoiceConfirmCard';
import { BrokRay } from './BrokRay';
import { AttachmentChip } from './AttachmentChip';

interface Props {
  messages: BrokUiMessage[];
  streaming: boolean;
  onConfirm: (toolCallId: string) => void;
  onReject: (toolCallId: string, note?: string) => void;
  onRueckfrage: (toolCallId: string, note: string) => void;
}

function AssistantText({ text }: { text: string }) {
  return (
    <div className="max-w-[85%] text-sm text-foreground">
      {text.split('\n').map((line, i) => (
        <span key={i}>
          {line}
          {i < text.split('\n').length - 1 && <br />}
        </span>
      ))}
    </div>
  );
}

function ToolRow({
  msg,
}: {
  msg: Extract<BrokUiMessage, { kind: 'tool' }>;
}) {
  if (msg.status === 'running') {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground italic">
        <BrokRay animated size={16} label="Brok arbeitet" />
        Brok prüft …
      </div>
    );
  }

  if (msg.toolName === 'get-pending-work' && msg.result != null) {
    return <PendingWorkCard data={msg.result as PendingWorkSummary} />;
  }
  if (msg.toolName === 'get-entity-summary' && msg.result != null) {
    return <EntitySummaryCard data={msg.result as EntitySummary} />;
  }
  return <ToolCallCard msg={msg} />;
}

export function BrokMessageList({ messages, streaming, onConfirm, onReject, onRueckfrage }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // scrollIntoView may not be available in jsdom (tests); guard defensively.
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, streaming]);

  // Detect typing state: streaming is true and last assistant message (if any) is still streaming
  const showTyping =
    streaming &&
    (messages.length === 0 ||
      messages[messages.length - 1]?.kind !== 'assistant' ||
      (messages[messages.length - 1] as Extract<BrokUiMessage, { kind: 'assistant' }>).id ===
        '__streaming__');

  return (
    <div className="flex-1 overflow-y-auto space-y-3 py-4">
      {messages.map((msg) => {
        if (msg.kind === 'user') {
          if (msg.hidden) return null;
          return (
            <div key={msg.id} className="flex flex-col items-end gap-1">
              <div className="max-w-[85%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
                {msg.text}
              </div>
              {msg.attachment && <AttachmentChip filename={msg.attachment.filename} />}
            </div>
          );
        }

        if (msg.kind === 'assistant') {
          return (
            <div key={msg.id} className="flex justify-start">
              <AssistantText text={msg.text} />
            </div>
          );
        }

        if (msg.kind === 'tool') {
          return (
            <div key={msg.id} className="flex justify-start w-full max-w-[85%]">
              <div className="w-full">
                <ToolRow msg={msg} />
              </div>
            </div>
          );
        }

        if (msg.kind === 'confirmation') {
          return (
            <div key={msg.id} className="flex justify-start w-full max-w-[85%]">
              <div className="w-full">
                {isInvoiceConfirmFacts(msg.facts) ? (
                  <InvoiceConfirmCard
                    msg={msg}
                    onApprove={onConfirm}
                    onReject={(toolCallId, note) => onReject(toolCallId, note)}
                    onRueckfrage={onRueckfrage}
                  />
                ) : (
                  <ConfirmCard
                    msg={msg}
                    onConfirm={onConfirm}
                    onReject={(toolCallId) => onReject(toolCallId)}
                  />
                )}
              </div>
            </div>
          );
        }

        return null;
      })}

      {showTyping && (
        <div className="flex items-center justify-start gap-2">
          <BrokRay animated size={16} label="Brok arbeitet" />
          <div className="text-xs text-muted-foreground italic">Brok schreibt …</div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
